import { useEffect, useRef, useState } from 'react';
import { CalendarDays, ChevronDown, Check } from 'lucide-react';

export type DashboardDateRange = '7d' | '30d' | '90d' | '180d' | 'all';

export const RANGE_OPTIONS: readonly { id: DashboardDateRange; label: string }[] = [
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: '180d', label: 'Last 180 days' },
  { id: 'all', label: 'All time' },
];

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Resolve a range id to a cutoff timestamp (inclusive lower bound). Returns
 * `null` for `all`, which callers interpret as "no lower bound".
 */
export function rangeStartMs(id: DashboardDateRange): number | null {
  if (id === 'all') return null;
  const days = id === '7d' ? 7 : id === '30d' ? 30 : id === '90d' ? 90 : 180;
  return Date.now() - days * DAY_MS;
}

export function rangeLabel(id: DashboardDateRange): string {
  return RANGE_OPTIONS.find((o) => o.id === id)?.label ?? 'Last 30 days';
}

function formatRangeDescriptor(id: DashboardDateRange): string {
  if (id === 'all') return 'All time';
  const start = rangeStartMs(id);
  const end = Date.now();
  if (!start) return rangeLabel(id);
  const fmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

export function DashboardDateRangePicker({
  value,
  onChange,
}: {
  value: DashboardDateRange;
  onChange: (next: DashboardDateRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-lg border vscode-border px-3 py-1.5 text-xs vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
      >
        <CalendarDays className="h-3.5 w-3.5" aria-hidden />
        <span className="tabular-nums">{formatRangeDescriptor(value)}</span>
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open ? (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-lg border vscode-border vscode-bg-elevated shadow-lg"
        >
          <ul className="py-1">
            {RANGE_OPTIONS.map((opt) => {
              const selected = opt.id === value;
              return (
                <li key={opt.id}>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    onClick={() => {
                      onChange(opt.id);
                      setOpen(false);
                      btnRef.current?.focus();
                    }}
                    className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary"
                  >
                    {opt.label}
                    {selected ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default DashboardDateRangePicker;
